import { Comparacao, Delta } from "./delta";
import { ChipReferencial } from "./status-chip";
import { useHistoricoCotacoes } from "@/hooks/use-cotacoes";
import { fmtComparacao, fmtKgBR, fmtMoedaBR, fmtDataCurtaBR } from "@/lib/formato-mercado";
import { PRODUTOS, chaveProduto, type ChaveProduto } from "@shared/mercado-produtos";
import type { MediaPorReferencialDTO, MinhasPorSemanaDTO, PeriodoHistorico } from "@shared/mercado-cotacoes";

/**
 * "Nas 2 semanas com placa de venda, a diferença média foi R$ 0,45/kg."
 *
 * A primeira frase vem inteira; as seguintes ficam curtas ("Na semana
 * publicada ao produtor, −3,2%."), porque o rodapé é uma linha só e repetir "a
 * diferença média foi" duas vezes empurraria o número para a segunda linha no
 * celular.
 */
function fraseDaMedia(m: MediaPorReferencialDTO, primeira: boolean): string {
  const trecho = m.referencial === "VENDA" ? "com placa de venda" : "publicada ao produtor";
  const quando = m.semanas === 1 ? `Na semana ${trecho}` : `Nas ${m.semanas} semanas ${trecho}`;
  const valor = fmtComparacao(m.media);
  return primeira ? `${quando}, a diferença média foi ${valor}.` : `${quando}, ${valor}.`;
}

/**
 * Semana a semana: a praça de um lado e — quando existem — as suas vendas do outro.
 *
 * Uma linha por semana ISO, mais recente em cima. As duas séries são casadas pelo
 * rótulo da semana, que é o mesmo dos dois lados por construção (`rotuloSemanaIso`).
 *
 * ⚠️ Hoje só a tela do PRODUTOR usa esta tabela (com `minhas`, as colunas "Você
 * recebeu" e "Diferença"). O comprador tem a matriz com os quatro produtos lado a
 * lado (`MatrizSemanal`), com o que ELE pagou.
 *
 * O histórico da praça é PÚBLICO (`/api/mercado/cotacoes/historico`) e é buscado
 * aqui dentro; as vendas, que são da fazenda, entram por prop. Duas telas com
 * duas cópias desta tabela divergiriam na primeira coluna nova.
 */
export function CardSemanaASemana({
  praca,
  produto,
  onProduto,
  periodo,
  minhas,
}: {
  praca: string | undefined;
  produto: ChaveProduto;
  onProduto: (p: ChaveProduto) => void;
  periodo: PeriodoHistorico;
  /** As vendas da fazenda. Ausente = lado do comprador: só a praça. */
  minhas?: MinhasPorSemanaDTO;
}) {
  const { data: historico } = useHistoricoCotacoes(praca, produto, periodo);
  const mostrarMinhas = minhas !== undefined;

  const porSemana = new Map((minhas?.semanas ?? []).map(s => [s.semana, s]));
  const semanas = historico?.semanas ?? [];

  return (
    <section className="rounded-lg border bg-card shadow-xs" data-testid="card-semana-a-semana">
      <header className="border-b px-3 py-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold">Semana a semana</p>
        <div className="flex flex-wrap gap-1.5">
          {PRODUTOS.map(p => (
            <button
              key={chaveProduto(p)}
              type="button"
              onClick={() => onProduto(p)}
              className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
                chaveProduto(p) === chaveProduto(produto)
                  ? "border-primary bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-pressed={chaveProduto(p) === chaveProduto(produto)}
              data-testid={`chip-produto-${p.variedade}-${p.qualidade ?? "s"}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </header>

      {semanas.length === 0 ? (
        <div className="px-3 py-8 text-center">
          <p className="text-sm font-medium">Sem publicação no período</p>
          <p className="text-xs text-muted-foreground mt-1">Escolha outro período ou outro produto.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground border-b">
                <th className="text-left font-semibold px-3 py-1.5">Semana</th>
                {/* A etiqueta DA SEMANA, e não a de hoje: sem esta coluna, uma
                    troca de placa entre duas semanas leria como queda de preço. */}
                <th className="text-left font-semibold px-3 py-1.5 hidden sm:table-cell">Praça publicou</th>
                <th className="text-right font-semibold px-3 py-1.5">Valor</th>
                {/* Some no celular: a seta do `Delta` desce para baixo do preço. */}
                <th className="text-right font-semibold px-3 py-1.5 hidden sm:table-cell">Variação</th>
                <th className="text-right font-semibold px-3 py-1.5">Você recebeu</th>
                <th className="text-right font-semibold px-3 py-1.5 hidden sm:table-cell">Diferença</th>
              </tr>
            </thead>
            <tbody>
              {semanas.map((s, i) => {
                const minha = porSemana.get(s.semana);
                return (
                  <tr
                    key={s.semana}
                    className={`border-b last:border-0 ${i === 0 ? "bg-sucesso-suave/40" : ""}`}
                    data-testid={`linha-semana-${s.semana}`}
                  >
                    <td className="px-3 py-2">
                      <span className="font-medium">{fmtDataCurtaBR(s.data)}</span>
                      {i === 0 && <span className="ml-1.5 text-[11px] text-muted-foreground">esta semana</span>}
                      {/* No celular a coluna some; o chip desce para baixo da data,
                          porque é ele que diz o que o número da linha significa. */}
                      <span className="sm:hidden block mt-0.5"><ChipReferencial referencial={s.referencial} /></span>
                    </td>
                    <td className="px-3 py-2 hidden sm:table-cell">
                      <ChipReferencial referencial={s.referencial} testId={`chip-referencial-semana-${s.semana}`} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="font-mono text-[18px] font-semibold tabular-nums whitespace-nowrap">{fmtMoedaBR(s.precoKg)}</span>
                      <span className="sm:hidden block"><Delta value={s.delta} /></span>
                    </td>
                    <td className="px-3 py-2 text-right hidden sm:table-cell"><Delta value={s.delta} /></td>
                    {mostrarMinhas && <td className="px-3 py-2 text-right">
                      {/* Semana sem venda fica EM BRANCO, não em zero: "não vendi
                          nada nesta semana" e "vendi a zero" são coisas
                          diferentes, e a segunda não existe. */}
                      {minha && minha.meuPreco != null ? (
                        <>
                          <span className="font-mono tabular-nums font-semibold whitespace-nowrap">{fmtMoedaBR(minha.meuPreco)}</span>
                          <span className="block text-[11px] text-muted-foreground">
                            {minha.vendas} {minha.vendas === 1 ? "venda" : "vendas"} · {fmtKgBR(minha.kg)}
                          </span>
                          <span className="sm:hidden block mt-0.5"><Comparacao valor={minha.diferenca} /></span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>}
                    {mostrarMinhas && <td className="px-3 py-2 text-right hidden sm:table-cell">
                      {minha && minha.meuPreco != null
                        ? <Comparacao valor={minha.diferenca} testId={`diferenca-semana-${s.semana}`} />
                        : <span className="text-muted-foreground">—</span>}
                    </td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <footer className="border-t bg-muted/40 px-3 py-2 text-xs text-muted-foreground space-y-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span>
            mínimo <b className="font-mono tabular-nums text-foreground">{historico?.min != null ? fmtMoedaBR(historico.min) : "—"}</b>
            {" · "}máximo <b className="font-mono tabular-nums text-foreground">{historico?.max != null ? fmtMoedaBR(historico.max) : "—"}</b>
            {" · "}no período <Delta value={historico?.variacaoPeriodo ?? null} />
          </span>
          <span>Rizoma</span>
        </div>
        {/* Uma frase POR REFERENCIAL, nunca uma média só. Semanas de placa ao
            produtor e semanas de placa de venda medem contra bases diferentes:
            a média das duas juntas seria um número sem unidade. */}
        {(minhas?.mediasPorReferencial ?? []).length > 0 && (
          <p data-testid="texto-medias-por-referencial">
            {(minhas?.mediasPorReferencial ?? []).map((m, i) => (
              <span key={m.referencial}>{i > 0 && " "}{fraseDaMedia(m, i === 0)}</span>
            ))}
          </p>
        )}
      </footer>
    </section>
  );
}
